import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { tr } from '../../../i18n/i18n';
import { ConfirmationModal } from '../';
import { AppTextarea } from '../../atoms';
import { ModalHandle } from '../index';

interface CancelInvoiceModalProps {
  assignInvoiceId?: string | null;
  onCancel: () => void;
  onConfirmCancel: (assign_invoice_id: string, cancellation_reason: string) => void;
  isLoading?: boolean;
}

const CancelInvoiceModal = forwardRef<ModalHandle, CancelInvoiceModalProps>(
  ({ assignInvoiceId, onCancel, onConfirmCancel, isLoading = false }, ref) => {
    const [cancellationReason, setCancellationReason] = useState('');
    const [error, setError] = useState('');
    const modalRef = useRef<ModalHandle>(null);

    // Expose openModal and closeModal methods to parent via ref
    useImperativeHandle(ref, () => ({
      openModal: () => {
        // Reset state when opening modal
        setCancellationReason('');
        setError('');
        modalRef.current?.openModal();
      },
      closeModal: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleConfirmCancel = () => {
      // Validate cancellation reason
      if (!cancellationReason.trim()) {
        setError(tr('g.x_cancellation_reason_is_required', 'Cancellation reason is required'));
        return;
      }

      if (cancellationReason.length > 150) {
        setError('Cancellation reason must not exceed 150 characters');
        return;
      }

      if (!assignInvoiceId) {
        setError('Invoice ID is missing');
        return;
      }

      // Clear any previous errors
      setError('');

      // Call parent handler with invoice ID and cancellation reason
      onConfirmCancel(assignInvoiceId, cancellationReason);
    };

    const handleCancellationReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCancellationReason(e.target.value);
      // Clear error when user starts typing
      if (error) {
        setError('');
      }
    };

    // Format invoice ID for display (handle # prefix)
    const displayInvoice = assignInvoiceId?.startsWith('#')
      ? assignInvoiceId
      : `#${assignInvoiceId}`;

    return (
      <ConfirmationModal
        ref={modalRef}
        title={tr('fe_modals.cancel_invoice_title', 'Cancel Invoice {{invoice}}?', {
          invoice: displayInvoice,
        })}
        severity="danger"
        buttonSeverity="danger"
        yesLabel={tr('fe_modals.yes_cancel_invoice', 'Yes, Cancel Invoice')}
        noLabel={tr('fe_modals.cancel', 'Cancel')}
        icon="pi-times-circle"
        width="500px"
        yesDisabled={isLoading || !cancellationReason.trim()}
        onYes={handleConfirmCancel}
        onNo={onCancel}
        content={
          <div className="flex flex-col gap-3 w-full">
            <p className="text-sm text-gray-600">
              {tr(
                'fe_modals.please_provide_a_reason_for_cancelling_this_invoice_thi',
                'Please provide a reason for cancelling this invoice. This action cannot be undone.',
              )}
            </p>

            <div className="flex flex-col gap-1">
              <AppTextarea
                value={cancellationReason}
                onChange={handleCancellationReasonChange}
                placeholder={tr(
                  'fe_modals.enter_cancellation_reason',
                  'Enter cancellation reason...',
                )}
                rows={4}
                maxLength={150}
                showCharCounter={true}
                disabled={isLoading}
                className="w-full"
                autoFocus
              />
              {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
          </div>
        }
      />
    );
  },
);

CancelInvoiceModal.displayName = 'CancelInvoiceModal';

export default CancelInvoiceModal;
